import { useEffect, useRef, useState } from "react";
import { LogLevel, LogLevelMap } from "../../../../constant/LogLevel";
import { Table } from "react-bootstrap";
import { ShrinkHeader } from "../../../Table/ShrinkHeader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretDown, faCaretRight } from "@fortawesome/free-solid-svg-icons";
import LoadingSpinner from "../../../Loading/LoadingSpinner";
import { ImportLogEntry } from "../../../../types/LayerAdmin/ImportLogEntry";
import styled from "styled-components";
import { LogQueryParams } from "../../../../types/LayerAdmin/ImportLogsQueryParams";
import FilterRow from "./FilterRow";
import { DateTime } from "luxon";
import { TableContainer } from "../../../../@ui/TableContainer";
import { useLocation, useParams } from "react-router-dom";
import React from "react";
import TableFooter from "../../../Table/Footer";
import { useLayerAdminService } from "../../../../hooks";

const MessageCell = styled.td`
  max-width: 200px; /* Set maximum width for the table cell */
  overflow: hidden; /* Hide overflowing content */
  text-overflow: ellipsis; /* Display ellipsis (...) for overflowed content */
  white-space: nowrap; /* Prevent text from wrapping */
`;

const TableHead = styled.thead``;

const ErrorText = styled.p`
  font-size: 12px;
`;

const DateCell = styled.td`
  text-wrap: nowrap;
`;

const StyledLink = styled.a`
  cursor: pointer;
  color: ${(props) => props.color || "blue"};
  &:hover {
    text-decoration: underline;
  }
`;

interface LogRow extends ImportLogEntry {
  id: symbol;
}

const ImportLogs = () => {
  const now = DateTime.now();
  const layerAdminService = useLayerAdminService();
  const { layerName } = useParams();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const importId = queryParams.get("importId");
  const startParam = queryParams.get("start");
  const start = startParam
    ? DateTime.fromSeconds(Number(startParam))
    : now.minus({ days: 10 }).startOf("day");

  const [params, setParams] = useState<LogQueryParams>({
    layerName: layerName!,
    importId: importId!,
    startDate: start,
    endDate: now,
    logLevel: LogLevel.INFORMATION,
  });

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [mainResponse, setMainResponse] = useState<LogRow[]>();
  const [logs, setLogs] = useState<LogRow[]>([]);

  const [showRows, setShowRows] = useState<symbol[]>([]);
  const [showAll, setShowAll] = useState(false);
  const tableRef = useRef<HTMLDivElement>(null);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const pageSize = 500;

  const getLogsPromise = (filterResult: LogQueryParams) => {
    const { layerName, importId, startDate, endDate, logLevel } = filterResult;
    return layerAdminService.GetLog(
      layerName,
      importId,
      startDate,
      endDate,
      logLevel,
    );
  };

  useEffect(() => {
    setLoading(true);
    getLogsPromise(params)
      .then((response) => {
        setMainResponse(response);
        setLoading(false);
      })
      .catch(() => {});
  }, []);

  const toggleShowRow = (symbol: symbol) => {
    if (showRows.includes(symbol)) {
      setShowRows(showRows.filter((row) => row !== symbol));
    } else {
      setShowRows([...showRows, symbol]);
    }
  };

  useEffect(() => {
    showAll ? setShowRows(logs.map((row) => row.id)) : setShowRows([]);
  }, [showAll]);

  const reloadLogs = (filerResult: LogQueryParams) => {
    setLoading(true);
    setParams(filerResult);
    getLogsPromise(filerResult)
      .then((response) => {
        setMainResponse(response);
        setLoading(false);
        setCurrentPage(1);
      })
      .catch(() => {});
  };

  const loadMoreLogs = () => {
    if (loadingMore) {
      return;
    }

    const newEnd = DateTime.now();

    setParams({ ...params, endDate: newEnd });
    setLoadingMore(true);

    getLogsPromise({
      ...params,
      endDate: newEnd,
      startDate: params.endDate,
    })
      .then((response) => {
        setLoadingMore(false);
        setMainResponse([
          ...(mainResponse ?? []),
          ...response.map((log: ImportLogEntry) => ({ ...log, id: Symbol() })),
        ]);
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (tableRef.current && loadingMore) {
      tableRef.current.scrollTop = tableRef.current.scrollHeight;
    }
  }, [tableRef.current, loadingMore]);

  useEffect(() => {
    if (mainResponse && currentPage != null) {
      const startIndex = (currentPage - 1) * pageSize;
      const paged = mainResponse.slice(startIndex, startIndex + pageSize);
      setLogs(paged.map((log: ImportLogEntry) => ({ ...log, id: Symbol() })));
    }
  }, [currentPage, mainResponse]);

  return (
    <>
      <FilterRow
        params={params}
        onSubmit={(params: any) => reloadLogs(params)}></FilterRow>
      <TableContainer ref={tableRef}>
        <Table className="bg-light" bordered hover>
          <TableHead>
            <tr>
              <ShrinkHeader className="p-2 text-white bg-primary">
                <div>
                  <FontAwesomeIcon
                    onClick={() => setShowAll(!showAll)}
                    size="xl"
                    icon={showAll ? faCaretDown : faCaretRight}
                  />
                </div>
              </ShrinkHeader>
              <ShrinkHeader className="p-2 text-white bg-primary">
                Timestamp
              </ShrinkHeader>
              <th className="p-2 text-white bg-primary">Message</th>
            </tr>
          </TableHead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="text-center">
                  <LoadingSpinner />
                </td>
              </tr>
            )}
            {!loading &&
              logs.map((log, index) => (
                <React.Fragment key={index}>
                  <tr>
                    <td>
                      <FontAwesomeIcon
                        onClick={() => toggleShowRow(log.id)}
                        size="xl"
                        icon={
                          showRows.includes(log.id) ? faCaretDown : faCaretRight
                        }
                      />
                    </td>
                    <DateCell>
                      {log.date.toFormat("dd/MM/yyyy HH:mm:ss")}
                    </DateCell>
                    <MessageCell>
                      {LogLevelMap.get(log.logLevel)} {log.message}
                    </MessageCell>
                  </tr>
                  {showRows.includes(log.id) && (
                    <tr className="w-100" key={index + "collapsibleRow"}>
                      <td colSpan={3}>
                        {log.logLevel === LogLevel.ERROR ? (
                          <ErrorText
                            style={{ fontSize: "12px" }}
                            className="text-danger">
                            {log.exception}
                          </ErrorText>
                        ) : (
                          <ErrorText>{log.message}</ErrorText>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            {loadingMore && (
              <tr>
                <td colSpan={6} className="text-center">
                  <LoadingSpinner />
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </TableContainer>
      {mainResponse && mainResponse.length > 0 && (
        <TableFooter
          currentPage={currentPage}
          setCurrentPage={(page: number) => setCurrentPage(page)}
          totalItems={mainResponse?.length}
          pageSize={500}
        />
      )}
      <div
        style={{ flex: "1" }}
        className="mt-1 d-flex flex-column w-100 justify-content-end">
        {!loading && (
          <StyledLink
            color={loadingMore ? "grey" : "blue"}
            onClick={() => loadMoreLogs()}>
            {loadingMore ? "Loading more ..." : "Load more logs"}
          </StyledLink>
        )}
      </div>
    </>
  );
};

export default ImportLogs;
